import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import type { CreativeEntry } from 'types/shared';
import creativesQueryKeys from '../../../creatives/api/queryKeys';
import { useAdTypes } from '../../api';
import type { LaunchPreviewValues } from './types';

export const useCreativeLookup = (): Map<string, CreativeEntry> => {
  const queryClient = useQueryClient();

  return useMemo(() => {
    const lookup = new Map<string, CreativeEntry>();
    const matches = queryClient.getQueriesData<CreativeEntry[]>({
      queryKey: creativesQueryKeys.allCreatives,
    });

    for (const [, data] of matches) {
      if (!Array.isArray(data)) continue;

      for (const creative of data) {
        if (creative?.id) {
          lookup.set(creative.id, creative);
        }
      }
    }

    return lookup;
  }, [queryClient]);
};

export const useAdTypeNames = (platform: string | undefined): Map<string, string> => {
  const { data: adTypes } = useAdTypes({
    params: { platform: platform ?? '' },
    config: { enabled: !!platform },
  });

  return useMemo(
    () => new Map((adTypes ?? []).map((type) => [type.code, type.name])),
    [adTypes],
  );
};

export const useCreativePreviewData = () => {
  const platform = useWatch<LaunchPreviewValues, 'platform'>({ name: 'platform' });
  const lookup = useCreativeLookup();
  const adTypeNames = useAdTypeNames(platform);

  return { lookup, adTypeNames };
};
